/**
 * eventAdapter - Transforms EventDetailPageState into normalized PageData
 *
 * This adapter is layout-agnostic. When new layouts are added, they use the same adapter.
 *
 * Note: This adapter is called within React's render cycle (from createLandingPageComponent),
 * so it can safely call functions that use hooks internally (EventBadges, EventStatuses).
 */
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import EventBadges from "@shared/modules/events/components/event-details/EventBadges/EventBadges.web";
import EventStatuses from "@ui-kit/data-display/EventStatuses/EventStatuses.web";
import type { EventDetailPageState } from "@app-contexts/public/features/event-detail/hooks/useEventDetailPage";
import type { PageData, AlertData } from "./types";

/**
 * Transforms event detail page state into normalized PageData
 */
export function eventAdapter(state: EventDetailPageState): PageData {
  const {
    eventData,
    productCategories,
    voucherData,
    user,
    publicBasket,
    isLoading,
    isLoggedIn,
    isReactNativeApp,
    isAttractionPage,
    isTablet,
    isSmallerThanSm,
    displayAllowedRegionsText,
    displayTicketsNotAvailableText,
    pastEvent,
    soldOutEvent,
    ticketsNotRequired,
    hasWaitlistItems,
    allowedRegions,
    eventInfo,
    venueInfo,
    viewMap,
    setViewMap,
    selectedTab,
    ticketsAuthorizedByPassword,
    scrollMargin,
    isBasketUpdating,
    cartSummaryRef,
    eventTabOptions,
    handleManualTabChange,
    contactDetailsModal,
    productModal,
    ticketModal,
    shareModal,
    getModalSubtitle,
  } = state;

  // Build badges using EventBadges (date, time, location, price)
  // Note: We pass null for tab-related props since badge clicks are handled separately
  // handleViewMap opens the map modal when location badge is clicked
  const rawBadges = eventData
    ? EventBadges({
        isSmallerThanSm,
        eventData,
        eventTabOptions: null,
        selectedTab: null,
        setSelectedTab: null,
        handleViewMap: setViewMap,
        isAttractionPage,
      })
    : [];

  const badges = rawBadges.map((badge) => ({
    id: String(badge.id),
    icon: badge.icon,
    text: badge.text,
    visible: badge.showBadge,
    dataTestId: badge.dataTestId,
  }));

  // Build statuses using EventStatuses (sold out, low inventory, multiple dates, waitlist)
  const rawStatuses = eventData ? EventStatuses({ event_data: eventData }) : [];

  const statuses = rawStatuses.map((status) => ({
    id: String(status.id),
    element: status.element,
    visible: status.showElement,
  }));

  // Build alerts array
  const alerts: AlertData[] = [];
  if (displayAllowedRegionsText && !isLoading) {
    alerts.push({
      type: "region",
      content: null,
    });
  }
  if (displayTicketsNotAvailableText && !isLoading) {
    alerts.push({
      type: "unavailable",
      content: null,
    });
  }

  // Build tabs from eventTabOptions
  const tabs = eventTabOptions.map((tab) => ({
    id: tab.id,
    title: tab.title,
    hash: tab.hash ?? null,
    visible: tab.showTab ?? true,
    onClick: tab.openModal,
  }));

  // Find description tab id for attraction pages
  const descriptionTabId =
    eventTabOptions.find((tab) => tab.hash === "additional-info")?.id ?? -1;

  return {
    pageType: "event",
    isAttraction: isAttractionPage,

    header: {
      title: eventData?.name ?? "",
      subtitle: eventData?.subtitle,
      image:
        eventData?.image_banner ||
        formatRelativeStaticPath("images/default-banner.png"),
      organizer: {
        name: eventData?.venue?.name ?? "",
        slug: eventData?.venue?.slug ?? "",
        onContact: contactDetailsModal.onOpen,
      },
      badges,
      statuses,
      saleStartsOn: eventData?.event_sale_starts_on,
      timezone: eventData?.timezone,
      favoriteData: { event: eventData, displayRight: true },
    },

    stickyTabs: {
      tabs,
      selectedTab,
      onTabChange: handleManualTabChange,
      cta: {
        label: eventData?.no_ticket_types ? "Free Event" : "Tickets",
        onClick: ticketModal.onOpen,
        venue: {
          name: eventData?.venue?.name ?? "",
          currency: eventData?.venue?.currency ?? "USD",
        },
        minPrice: undefined,
        maxPrice: undefined,
      },
      headerAction: {
        title: eventData?.name ?? "",
        locationText:
          eventData?.location && !eventInfo.eventVirtualType
            ? `${eventData.location.name} ${eventData.location.street_name}, ${eventData.location.city}`
            : undefined,
        dateText: eventData?.starts_on,
        onAction: ticketModal.onOpen,
        visible: !isTablet,
      },
    },

    mainContent: {
      description: eventData?.description,
      restrictions: eventData?.restrictions,
      refundPolicy:
        eventData?.refund_policy || eventData?.venue?.default_refund_policy,
      venueInfo: {
        id: venueInfo.venueId,
        slug: venueInfo.venueSlug,
        name: venueInfo.venueName,
        description: venueInfo.venueDescription,
      },
      locationInfo: {
        id: eventInfo.eventId,
        locationName: eventInfo.eventLocationName,
        address: eventInfo.eventAddress,
        latLng: eventInfo.eventLatLng,
        virtualType: eventInfo.eventVirtualType,
        accommodationUrl: eventInfo.eventAccommodationUrl,
      },
      alerts,
    },

    footer: {
      visible: isAttractionPage,
      cartSummaryRef,
      venue: eventData?.venue,
    },

    modals: {
      contact: contactDetailsModal,
      tickets: ticketModal,
      products: productModal,
      share: shareModal,
    },

    features: {
      eventData,
    },

    ui: {
      isLoading,
      isTablet,
      isMobile: isSmallerThanSm,
      scrollMargin,
      isReactNativeApp,
    },

    attraction: isAttractionPage
      ? {
          config: eventData?.attraction_event_config ?? [],
          basket: publicBasket,
          isBasketUpdating,
          descriptionTabId,
        }
      : undefined,

    rawData: {
      event: eventData,
      productCategories,
      voucherData,
      user,
      isLoggedIn,
      ticketsAuthorizedByPassword,
      pastEvent,
      soldOutEvent,
      ticketsNotRequired,
      hasWaitlistItems,
      allowedRegions,
      viewMap,
      setViewMap,
    },

    getModalSubtitle,
  };
}
